<template>
  <label for="image" class="block text-sm font-medium text-gray-700 mb-1">
    Pre-session materials
  </label>
  <span class="text-sm text-gray-700">
    Upload clinical guidelines, case studies, or relevant materials for pre-session preparation.
  </span>
  <div class="text-sm text-gray-900 sm:mt-0 grid grid-cols-3 gap-x-8 w-full">
    <div class="col-span-2">
      <ul role="list" class="rounded-md w-full mt-2 grid grid-cols-2 gap-x-8">
        <li
          class="py-1 flex items-center justify-between text-sm"
          v-for="(file, index) in allFiles"
          :key="`file-${index}`"
        >
          <div class="w-0 flex-1 flex items-center">
            <UploadIcon class="flex-shrink-0 h-4 w-4 text-gray-300" aria-hidden="true" />
            <span class="ml-2 flex-1 w-0 text-gray-800 font-bold truncate">{{ file.name }}</span>
          </div>
          <div
            v-if="deletingFiles.includes(file.id)"
            class="w-5 h-5 border-vice-teal-500 border-2 rounded-full border-t-0 animate-spin"
          ></div>
          <XIcon
            v-else
            data-cy="delete-session-files"
            class="w-4 text-gray-300 cursor-pointer"
            @click="deleteFile(file)"
          />
        </li>
      </ul>
    </div>
    <div class="col-span-1">
      <div class="flex flex-col w-full relative">
        <button
          type="button"
          class="
            cursor-pointer
            relative
            flex
            justify-center
            items-center
            flex-col
            w-full
            border-2 border-gray-300 border-dashed
            rounded-md
            pt-10
            pb-10
            text-center
            hover:border-gray-400
            group
          "
        >
          <div class="flex items-center justify-center">
            <div class="flex space-x-2">
              <div class="flex items-center">
                <UploadIcon class="w-7 text-gray-300 group-hover:text-gray-400" />
              </div>
              <div class="flex items-center">
                <div class="text-left">
                  <span class="text-sm font-medium text-gray-700 group-hover:text-gray-500 block">
                    <span class="font-bold">
                      <span class="text-vice-teal-700 underline">Click to upload</span>
                      or drag and drop
                    </span>
                  </span>

                  <span
                    class="mt-2 text-sm font-medium text-gray-500 group-hover:text-gray-400 block"
                  >
                    PDF, PNG, JPG, or DOCX (max. 3MB)
                  </span>
                </div>
              </div>
            </div>
          </div>
          <input
            data-cy="session-files"
            type="file"
            multiple
            id="file"
            accept=".png, .pdf, .jpeg, .jpg, .docx"
            @change="onFileAttached"
            class="cursor-pointer opacity-0 absolute h-full w-full"
          />
        </button>
        <div
          v-if="isUploading && errorMessage == ''"
          class="w-full bg-gray-200 rounded-md mt-1 h-2 absolute -bottom-2.5"
        >
          <div
            class="bg-vice-teal-500 h-2 rounded-md transition-all"
            :style="{ width: `${(loaded * 100) / total}%` }"
          ></div>
        </div>
      </div>
    </div>
  </div>
  <span v-if="errorMessage" class="text-red-600 text-sm">
    {{ errorMessage }}
  </span>
</template>

<script>
import { XIcon } from '@heroicons/vue/solid';
import { Inertia } from '@inertiajs/inertia';
import UploadIcon from '@/Components/Icons/UploadIcon.vue';

export default {
  name: 'MultipleFilesInput',
  components: {
    XIcon,
    UploadIcon,
  },
  data() {
    return {
      deletingFiles: [],
      isUploading: false,
      loaded: 0,
      total: 0,
      errorMessage: '',
      maxFileSizeInBytes: 3145728,
      allowedTypes: [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/octet-stream', // .docx
      ],
    };
  },
  methods: {
    onFileAttached(e) {
      this.uploadFiles(
        Object.values(e.target.files),
        this.$page.props.session.id,
        this.$page.props.session.unique_id,
      );

      Array.from(document.getElementById('file').files).forEach((file) => {
        // Check file extension
        if (!this.allowedTypes.includes(file.type)) {
          this.isUploading = false;
          this.errorMessage =
            'Sorry, but your file is not allowed to upload! Please upload PDF, PNG, JPG, or DOCX files.';
        }

        // Check file size
        if (file.size > this.maxFileSizeInBytes) {
          this.isUploading = false;
          this.errorMessage =
            'Sorry, but your file is too large to upload! The maximum file size is 3MB.';
        }
      });
    },
    async uploadFiles(files, sessionId, uniqueId) {
      this.errorMessage = '';
      this.isUploading = true;
      const vm = this;
      const fileData = new FormData();
      files.forEach((file) => {
        fileData.append('session_files[]', file);
      });
      fileData.append('id', sessionId);
      fileData.append('unique_id', uniqueId);

      return axios
        .post(route('save.session.files'), fileData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress(progress) {
            vm.loaded = progress.loaded;
            vm.total = progress.total;
          },
        })
        .then(() => {
          Inertia.reload({ only: ['session'] });
        })
        .catch((error) => {
          if (error.response) {
            console.log('', error.response);
          }
        })
        .finally(() => {
          vm.isUploading = false;
          vm.loaded = 0;
          vm.total = 0;
        });
    },

    deleteFile(file) {
      this.deletingFiles.push(file.id);
      const deleteParameters = new FormData();
      deleteParameters.append('id', file.id);
      deleteParameters.append('path', file.path);

      return axios
        .post(route('delete.session.file'), deleteParameters)
        .then((response) => {
          console.log('response', response);
          Inertia.reload({ only: ['session'] });
        })
        .catch((error) => {
          console.log('error', error);
        });
    },
  },
  computed: {
    allFiles() {
      return this.$page.props.session.files;
    },
  },
};
</script>

<style scoped></style>
